import { useState } from 'react'
import clsx from 'clsx'
import { CheckCircle2, XCircle } from 'lucide-react'
import MetricsHistorySection from '../components/sections/MetricsHistorySection'
import { formatDateTime } from '../../../utils/format'
import { BatteryIndicator, NetworkIndicator } from './mobileUi'
import AgentVersionModal from './AgentVersionModal'
import {
  formatBytes, timeAgo, AGENT_UPDATE_STATE, agentTargetSourceLabel, formatUntil, isKioskSuspended, isMdmPaused,
} from './mobileUtils'

function formatUptime(ms) {
  if (ms == null) return '—'
  const s = Math.floor(ms / 1000)
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}min` : `${m}min`
}

/** Situação do quiosque com o kioskState do agente ≥ 1.1 (cai no kioskActive nos agentes antigos). */
function KioskValue({ state }) {
  const st = state?.kioskState
  if (isKioskSuspended(state)) {
    return <span className="text-amber-700">Fora do quiosque{state.kioskSuspendedUntil ? ` até ${formatUntil(state.kioskSuspendedUntil)}` : ''}</span>
  }
  if (st === 'crashloop') return <span className="text-red-600">App do quiosque travando</span>
  if (st === 'updating') return <span className="text-blue-700">Atualizando o app do quiosque</span>
  if (st === 'paused') return <span className="text-amber-700">Suspenso (MDM pausado)</span>
  return <Flag value={st ? st === 'running' : state?.kioskActive} yes="Ativo" no="Inativo" />
}

function Flag({ value, yes = 'Sim', no = 'Não' }) {
  if (value == null) return <span className="text-gray-400">—</span>
  return value
    ? <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="w-4 h-4" />{yes}</span>
    : <span className="inline-flex items-center gap-1 text-red-600"><XCircle className="w-4 h-4" />{no}</span>
}

export default function OverviewTab({ device, state }) {
  const [versionOpen, setVersionOpen] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const m = state?.metrics || {}
  const storageUsed = m.storage?.totalBytes != null && m.storage?.freeBytes != null
    ? m.storage.totalBytes - m.storage.freeBytes : null
  const memUsed = m.memory?.totalBytes != null && m.memory?.availBytes != null
    ? m.memory.totalBytes - m.memory.availBytes : null

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Aparelho">
          <Row label="Fabricante / modelo" value={[state?.manufacturer, state?.model].filter(Boolean).join(' ') || device.model || '—'} />
          <Row label="Android" value={state?.androidVersion ? `Android ${state.androidVersion}` : '—'} />
          <Row label="Serial" value={state?.serial || device.serialNumber || '—'} mono />
          <Row label="ID do aparelho" value={state?.fingerprint || '—'} mono />
          <Row
            label="Agente MDM"
            value={
              <span className="inline-flex items-center gap-2">
                {state?.agentVersion || '—'}
                {state && state.agentUpdateState !== 'none' && (
                  <span className={clsx('px-1.5 py-0.5 rounded text-xs', AGENT_UPDATE_STATE[state.agentUpdateState]?.className)}>
                    {AGENT_UPDATE_STATE[state.agentUpdateState]?.label}
                  </span>
                )}
                {state && (
                  <button type="button" onClick={() => setVersionOpen(true)} className="text-xs text-theme-primary hover:underline">
                    alterar
                  </button>
                )}
              </span>
            }
          />
          {state?.agentTargetVersion && (
            <Row label="Versão-alvo" value={`${state.agentTargetVersion} (${agentTargetSourceLabel(state)})`} />
          )}
          {state?.agentUpdateError && state.agentUpdateState !== 'upToDate' && (
            <Row label="Última falha ao atualizar" value={<span className="text-red-600" title={state.agentUpdateError}>{state.agentUpdateError}</span>} />
          )}
          {feedback && (
            <div className={clsx('py-1.5 text-xs', feedback.type === 'ok' ? 'text-green-700' : 'text-red-600')}>{feedback.text}</div>
          )}
          <Row label="Cadastrado em" value={state?.enrolledAt ? formatDateTime(state.enrolledAt) : '—'} />
          <Row label="Administrador do aparelho" value={<Flag value={m.isDeviceOwner} yes="Device Owner" no="Não é Device Owner" />} />
          <Row label="Acessibilidade (controle remoto)" value={<Flag value={state?.accessibilityEnabled ?? m.accessibilityEnabled} yes="Ativa" no="Desativada" />} />
        </Card>

        <Card title="Agora">
          <Row label="Bateria" value={<BatteryIndicator level={state?.batteryLevel} charging={state?.charging} />} />
          {m.battery?.temperatureC != null && <Row label="Temperatura da bateria" value={`${m.battery.temperatureC.toFixed(1)} °C`} />}
          <Row label="Rede" value={<NetworkIndicator type={state?.networkType} signalLevel={state?.signalLevel} ssid={m.network?.ssid} />} />
          {m.network?.ssid && <Row label="Wi-Fi" value={m.network.ssid} />}
          {m.network?.ip && <Row label="IP local" value={m.network.ip} mono />}
          <Row label="VPN" value={state?.vpnEnabled === false
            ? <span className="text-gray-500">Desligada pela política</span>
            : <Flag value={state?.vpnConnected} yes="Conectada" no="Desconectada" />} />
          <Row label="Armazenamento" value={storageUsed != null ? `${formatBytes(storageUsed)} de ${formatBytes(m.storage.totalBytes)}` : '—'} />
          <Row label="Memória" value={memUsed != null ? `${formatBytes(memUsed)} de ${formatBytes(m.memory.totalBytes)}` : '—'} />
          <Row label="Ligado há" value={formatUptime(m.uptimeMs)} />
          <Row label="Tela" value={m.screenOn == null ? '—' : m.screenOn ? 'Ligada' : 'Desligada'} />
          <Row label="App em primeiro plano" value={m.foregroundApp || '—'} mono />
          <Row label="Quiosque" value={<KioskValue state={state} />} />
          {isMdmPaused(state) && (
            <Row label="MDM" value={<span className="text-amber-700">Pausado{state.mdmPausedUntil ? ` até ${formatUntil(state.mdmPausedUntil)}` : ''}</span>} />
          )}
          {state?.localAdminActive != null && (
            <Row label="Modo técnico" value={state.localAdminActive ? <span className="text-amber-700">Aberto no tablet</span> : 'Fechado'} />
          )}
          {state?.localRemovedAt && (
            <Row label="MDM removido no tablet" value={<span className="text-red-600">{formatDateTime(state.localRemovedAt)}</span>} />
          )}
          <Row label="Último contato" value={timeAgo(device.lastSeenAt || state?.lastSeenAt)} />
        </Card>
      </div>

      <MetricsHistorySection deviceId={device.id} currentMetricsJson={device.metricsJson} />

      {state && (
        <AgentVersionModal
          isOpen={versionOpen}
          onClose={() => setVersionOpen(false)}
          state={{ ...state, name: state.name || device.name }}
          onFeedback={setFeedback}
        />
      )}
    </div>
  )
}

function Card({ title, children }) {
  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-2">{title}</h3>
      <dl className="divide-y divide-gray-100">{children}</dl>
    </section>
  )
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <dt className="text-gray-500">{label}</dt>
      <dd className={`text-gray-900 text-right truncate ${mono ? 'font-mono text-xs' : ''}`}>{value ?? '—'}</dd>
    </div>
  )
}
